'use client';
import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import arrow from '../../../public/images/homePagev2/arrow-black.png';
import mobileRobot from '../../../public/webp/homePagev2/mobile-robot.webp';
import LogoAnimation from '../ui/LogoAnimation';
import phone from '../../../public/svg/footer/phone.svg';
import email from '../../../public/svg/footer/mail.svg';
import { contactDetails, logoIcons, OurWork } from '../utils/Constant';

// @ts-ignore
import 'swiper/css';
// @ts-ignore
import 'swiper/css/navigation';
// @ts-ignore
import 'swiper/css/pagination';
// @ts-ignore
import 'swiper/css/scrollbar';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import Faqs from '../ui/Faqs';
import Container from '../ui/Container';
import Link from 'next/link';
import styled from 'styled-components';
import Marquee from 'react-fast-marquee';
import SocialMedia from '../layout/footer/SocialMedia';
import SectionHeader from '../ui/SectionHeader';
import OurWorkCard from '../common/OurWorkCard';
import Button from '../ui/Button';
import ClientTestimonial from '../ui/ClientTestimonial';
import ProjectInquiry from '../contact/ProjectInquiry';
import { homePageData } from '../utils/page-data/homePage';
import ClutchWidget from '../analytics/ClutchWidget';
import HeroSwiper from './hero-section/HeroSwiper';

const HighlitedDescription = styled.h2``;
const HomePageV2 = () => {
  const prevRef = useRef<HTMLButtonElement | null>(null);
  const nextRef = useRef<HTMLButtonElement | null>(null);
  const logos = logoIcons.map((image) => image?.src);
  const sectionsRef = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      sectionsRef.current.forEach((section) => {
        if (!section) return;

        const rect = section.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        // Calculate if section is in viewport
        const isInViewport = rect.top < windowHeight && rect.bottom > 0;

        if (isInViewport) {
          // Calculate scroll progress within the section
          const scrollProgress =
            (windowHeight - rect.top) / (windowHeight + rect.height);

          // Services section with alternating columns
          const oddGroups = section.querySelectorAll(
            '[data-parallax-group="odd"]',
          );
          const evenGroups = section.querySelectorAll(
            '[data-parallax-group="even"]',
          );

          oddGroups.forEach((group) => {
            const element = group as HTMLElement;
            const parallaxY = scrollProgress * 20;
            element.style.transform = `translateY(${parallaxY - 20}px)`;
          });

          evenGroups.forEach((group) => {
            const element = group as HTMLElement;
            const parallaxY = scrollProgress * -20;
            element.style.transform = `translateY(${parallaxY + 20}px)`;
          });
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const addToRefs = (el: HTMLElement | null, index: number) => {
    sectionsRef.current[index] = el;
  };
  const [activeTab, setActiveTab] = useState('real-estate');

  const currentTab =
    homePageData?.ourIndustries.find((tab) => tab.id === activeTab) ||
    homePageData?.ourIndustries[0];

  return (
    <>
      <div className="bg-white pt-6 xl:pt-0">
        <HeroSwiper />
      </div>
      <div className="bg-white pt-10">
        <Container>
          <div className="flex flex-col items-center gap-[25px] lg:hidden xl:flex-row 5xl:rounded-[24px]">
            <span className="max-w-[400px] text-center font-opt text-base font-normal leading-5 text-optDesc xl:text-start">
              {homePageData?.trustedClient}
            </span>
            <Marquee
              speed={55}
              pauseOnHover={true}
              gradient={true}
              gradientColor="white"
            >
              <div className="flex items-center justify-between gap-8 md:gap-10">
                {logoIcons?.map((item, index) => (
                  <div
                    key={index}
                    className="first-of-type::md:ml-10 grid place-items-center justify-center first-of-type:ml-8"
                  >
                    <Image
                      src={item}
                      alt="icon"
                      width={150}
                      height={50}
                      loading="lazy"
                      className="h-[50px] max-w-[100px] object-contain"
                    />
                  </div>
                ))}
              </div>
            </Marquee>
          </div>
          <div className="hidden flex-col items-center gap-[25px] lg:flex xl:flex-row xl:justify-between 5xl:rounded-[24px]">
            <span className="max-w-[400px] text-center font-opt text-base font-normal leading-6 text-optDesc xl:text-start">
              {homePageData?.trustedClient}
            </span>
            <LogoAnimation logo={logos} />
          </div>
        </Container>
      </div>
      <div
        style={{ backgroundSize: '100% 100%' }}
        className="my-[60px] bg-our-expertise bg-cover bg-no-repeat 4xl:my-[100px]"
      >
        <div className="flex flex-col gap-8">
          <Container>
            <SectionHeader
              headingText={homePageData?.ourExpertiseTitle}
              headingStyle="text-start"
            />
          </Container>
          <div className={`w-full`}>
            <Swiper
              spaceBetween={12}
              slidesPerView={1.2}
              slidesOffsetAfter={120}
              slidesOffsetBefore={10}
              breakpoints={{
                540: {
                  slidesPerView: 1.6,
                  spaceBetween: 30,
                },
                1439: {
                  spaceBetween: 50,
                },
                1560: {
                  slidesPerView: 1.8,
                  spaceBetween: 50,
                },
              }}
              speed={1000}
              className="!h-auto w-full"
            >
              {homePageData?.ourExpertise &&
                homePageData?.ourExpertise.map((item, index) => (
                  <SwiperSlide
                    key={index}
                    className="m-2 !flex !h-auto items-stretch rounded-xl bg-white p-5 shadow-md md:m-4 xl:p-8 3xl:p-12"
                  >
                    <div className="flex w-full flex-col-reverse items-center justify-between gap-6 xl:flex-row 4xl:gap-[65px]">
                      <div className="flex h-full w-full max-w-[600px] flex-col justify-between gap-5 xl:gap-[40px]">
                        <div className="flex flex-col gap-3 md:gap-4">
                          <h3 className="font-opt text-lg font-semibold leading-6 text-primary md:text-xl">
                            {item?.title}
                          </h3>
                          <p className="font-opt text-base font-normal leading-5 text-optDesc">
                            {item?.description}
                          </p>
                          <ul className="ml-3 flex list-disc flex-col gap-3 text-primary lg:ml-10">
                            {item?.techBenefits &&
                              item?.techBenefits.map((benefitsItem, idx) => (
                                <li
                                  key={idx}
                                  className="font-opt text-base font-medium leading-5 text-primary md:text-lg"
                                >
                                  {benefitsItem?.title}
                                  <span className="text-base font-normal leading-5 text-optDesc">
                                    &nbsp;{benefitsItem?.description}
                                  </span>
                                </li>
                              ))}
                          </ul>
                          <p className="font-opt text-base font-normal leading-5 text-optDesc">
                            {item?.subDescription}
                          </p>
                        </div>
                        <Button
                          btnName={item?.button}
                          redirectionLink={item?.buttonLink}
                          isBackgroung={index % 2 === 0 ? true : false}
                        />
                      </div>
                      <div className="xxl:w-[500px]">
                        <Image
                          src={item?.img}
                          alt="img"
                          width={698}
                          height={600}
                          loading="lazy"
                          sizes="(max-width: 640px) 90vw, (max-width: 768px) 90vw, (max-width: 1024px) 90vw, (max-width: 1280px) 500px, 500px"
                          className="w-full rounded-[14px] object-contain"
                        />
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
            </Swiper>
          </div>
        </div>
      </div>
      <Container className="mb-[60px] w-full max-w-[1680px] 4xl:mb-[100px]">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 xl:gap-[45px]">
          {OurWork &&
            OurWork.map((item, i) => <OurWorkCard key={i} data={item} />)}
        </div>
      </Container>
      <div
        style={{ backgroundSize: '100% 100%', backgroundColor: '#000' }}
        className="relative mb-[60px] bg-our-expertise bg-cover bg-no-repeat py-[60px] 4xl:mb-[100px] 4xl:py-[100px]"
      >
        <Container className="flex flex-col gap-10 4xl:gap-[60px]">
          <SectionHeader
            headingText={homePageData?.aiSectionHeqading}
            descriptionText={homePageData?.aiSectionDescription}
            descriptionStyle="text-start md:text-center"
            className="mx-auto max-w-[1000px] items-center"
            headingStyle="text-start"
            isSectionDark={true}
          />
          <div className="flex flex-col gap-6 md:hidden">
            <div className="">
              <Image
                src={mobileRobot}
                alt="robot"
                width={250}
                height={500}
                loading="lazy"
                className="mx-auto"
              />
            </div>
            <div className="hide-scrollbar flex gap-7 overflow-auto">
              {homePageData?.aiServices &&
                homePageData?.aiServices.map((item, index) => (
                  <div
                    key={index}
                    className="flex w-full min-w-[320px] flex-1 flex-col gap-4"
                  >
                    <h3 className="font-opt text-lg font-medium leading-6 text-white">
                      {item?.title}
                    </h3>
                    <p className="font-opt text-base font-normal leading-5 text-subtle">
                      {item?.description}
                    </p>
                  </div>
                ))}
            </div>
          </div>
          <div className="hidden grid-cols-2 gap-8 md:grid lg:grid-cols-3">
            <div className="flex w-full max-w-[350px] flex-col gap-10">
              {homePageData?.aiServices &&
                homePageData?.aiServices.slice(0, 3).map((item, index) => (
                  <div key={index} className="flex flex-col gap-4">
                    <h3 className="font-opt text-lg font-medium leading-6 text-white">
                      {item?.title}
                    </h3>
                    <p className="font-opt text-base font-normal leading-5 text-subtle">
                      {item?.description}
                    </p>
                  </div>
                ))}
            </div>
            <div className="sticky top-[100px] hidden h-min lg:block">
              <Image
                alt="robot"
                src={mobileRobot}
                width={180}
                height={210}
                sizes="180px"
                className="mx-auto h-auto max-w-[180px]"
              />
            </div>
            <div className="ml-auto flex w-full max-w-[350px] flex-col gap-10">
              {homePageData?.aiServices &&
                homePageData?.aiServices.slice(3).map((item, index) => (
                  <div key={index} className="flex flex-col gap-4">
                    <h3 className="font-opt text-lg font-medium leading-6 text-white">
                      {item?.title}
                    </h3>
                    <p className="font-opt text-base font-normal leading-5 text-subtle">
                      {item?.description}
                    </p>
                  </div>
                ))}
            </div>
          </div>
          <div className="flex flex-col gap-8 4xl:gap-[60px]">
            <SectionHeader
              headingText={homePageData?.technologyWeWorkTitle}
              headingStyle="text-start"
              isSectionDark={true}
            />
            <div
              ref={(el) => addToRefs(el, 0)}
              className="hidden h-full w-full grid-cols-6 gap-6 3xl:grid"
            >
              {homePageData?.technologyWeWork &&
                homePageData?.technologyWeWork.map((item, index) => {
                  const isOddColumn = index % 2 === 0;
                  return (
                    <div
                      className={`w-full rounded-[24px] bg-overly bg-cover bg-no-repeat transition-transform duration-500 ease-out`}
                      data-parallax-group={isOddColumn ? 'odd' : 'even'}
                      key={index}
                    >
                      <div className="parallax-card relative grid h-full w-full rounded-[20px] bg-cover p-[8px] transition-transform duration-100 ease-out grid-stack">
                        <Image
                          src={item?.techImg}
                          alt="technology"
                          width={254}
                          height={339}
                          loading="lazy"
                          className="aspect-auto !rounded-xl"
                        />
                        <div className="absolute inset-0 rounded-xl bg-techCardGradient"></div>
                        <span className="relative z-30 mt-auto p-[14px] font-opt text-[20px] font-medium leading-[24px] text-white">
                          {item?.techName}
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>
            <div className="hide-scrollbar flex h-full w-full gap-6 overflow-x-auto 3xl:hidden">
              {homePageData?.technologyWeWork &&
                homePageData?.technologyWeWork.map((item, index) => (
                  <div
                    className={`rounded-[24px] bg-overly bg-cover bg-no-repeat transition-transform duration-500 ease-out ${index % 2 === 0 ? 'mt-4' : ''} h-max`}
                    key={index}
                  >
                    <div className="parallax-card grid h-full w-[200px] rounded-[20px] bg-cover p-[8px] transition-transform duration-100 ease-out grid-stack">
                      <Image
                        src={item?.techImg}
                        alt="technology"
                        width={254}
                        height={339}
                        loading="lazy"
                        className="aspect-auto rounded-xl"
                      />
                      <span className="mt-auto p-[14px] font-opt text-[20px] font-medium leading-[24px] text-white">
                        {item?.techName}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </Container>
      </div>
      <Container className="mb-[60px] flex flex-col gap-6 md:gap-8 4xl:mb-[100px] 4xl:gap-10">
        <SectionHeader
          headingText={homePageData?.ourIndustriesTitle}
          headingStyle="text-start"
        />
        <div className="hide-scrollbar flex w-full items-center gap-4 overflow-auto px-2 py-2">
          {homePageData?.ourIndustries &&
            homePageData?.ourIndustries.map((item, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setActiveTab(item?.id)}
                className={`flex w-max items-center justify-center gap-1 text-nowrap rounded-[50px] border px-[14px] py-[8px] font-opt text-sm font-normal leading-4 transition-all duration-200 ease-in ${activeTab === item?.id ? 'border-transparent bg-lightBlue text-white' : 'border-optDesc text-optDesc hover:border-transparent hover:bg-lightBlue hover:text-white'}`}
              >
                <span className="mt-0.5">{item?.label}</span>
              </button>
            ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentTab?.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{
              duration: 0.3,
              ease: [0.4, 0, 0.2, 1],
            }}
            layout
            className="flex flex-col justify-between gap-10 lg:flex-row lg:items-end"
          >
            <div className="flex w-full max-w-full flex-col gap-6 lg:max-w-[650px] xxl:max-w-[800px]">
              <div className="flex flex-col gap-2.5">
                <h3 className="font-opt text-2xl font-semibold leading-8 text-primary md:text-3xl md:leading-9">
                  {currentTab?.title}
                </h3>
                <p className="font-opt text-base font-normal leading-5 text-optDesc">
                  {currentTab?.description}
                </p>
              </div>
              <div className="mx-auto block h-full w-full max-w-[400px] lg:hidden">
                <Image
                  src={currentTab?.image}
                  alt="industries"
                  width={828}
                  height={993}
                  priority
                  fetchPriority="high"
                  className="rounded-xl"
                />
              </div>

              <div className="flex flex-col gap-6 rounded-xl bg-snowGray p-4 md:p-8">
                <h4 className="font-opt text-xl font-medium leading-6 text-lightBlue">
                  {currentTab?.sectionTitle}
                </h4>
                <ul className="flex flex-col gap-3">
                  {currentTab?.features.map((list, index) => (
                    <li key={index} className="flex items-center gap-4">
                      <Image
                        src="/svg/listarrow.svg"
                        alt="tick"
                        width={20}
                        height={20}
                        loading="lazy"
                      />
                      <p className="font-opt text-base font-normal leading-5 text-optDesc">
                        {list}
                      </p>
                    </li>
                  ))}
                </ul>
                <Button
                  btnName={currentTab?.LearnMore}
                  redirectionLink={currentTab?.link}
                  isBackgroung={false}
                />
              </div>
            </div>
            <div className="hidden lg:block">
              <Image
                src={currentTab?.image}
                alt="industries"
                width={828}
                height={993}
                priority
                fetchPriority="high"
                sizes="(max-width: 640px) 90vw, 400px"
                className="h-auto w-full max-w-[400px] rounded-xl"
              />
            </div>
          </motion.div>
        </AnimatePresence>
      </Container>
      <div className="mb-[60px] 4xl:mb-[100px]">
        <div className="flex flex-col gap-8 3xl:gap-10 4xl:gap-[80px]">
          <Container className="flex flex-col justify-between gap-5 md:flex-row md:items-center md:gap-0">
            <SectionHeader
              headingText={homePageData?.deployedProjectsTitle}
              headingStyle="text-start"
            />
            <div className="flex w-max items-center gap-4 md:gap-6">
              <button
                ref={prevRef}
                className="custom-prev group flex h-11 w-11 items-center justify-center rounded-full border border-primary/20 bg-white shadow-md transition-all duration-300 hover:bg-black-100-alpha hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Image
                  src={arrow}
                  alt="Previous"
                  width={20}
                  height={20}
                  className="rotate-180 transition-transform duration-300 group-hover:scale-110"
                />
              </button>

              <button
                ref={nextRef}
                className="custom-next group flex h-11 w-11 items-center justify-center rounded-full border border-primary/20 bg-white shadow-md transition-all duration-300 hover:bg-black-100-alpha hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Image
                  src={arrow}
                  alt="Next"
                  width={20}
                  height={20}
                  className="transition-transform duration-300 group-hover:scale-110"
                />
              </button>
            </div>
          </Container>
          <div className="w-full">
            <Swiper
              spaceBetween={12}
              slidesPerView={1.2}
              modules={[Autoplay, Navigation]}
              autoplay={{
                delay: 1500,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              navigation={{
                nextEl: '.custom-next',
                prevEl: '.custom-prev',
              }}
              breakpoints={{
                540: {
                  slidesPerView: 1.6,
                  spaceBetween: 30,
                },
                1439: {
                  slidesPerView: 2.3,
                  spaceBetween: 40,
                },
              }}
              centeredSlides={true}
              speed={1000}
              loop={true}
              className="!h-auto w-full !p-4"
            >
              {homePageData?.deployedProjectsList &&
                homePageData?.deployedProjectsList.map((item, index) => (
                  <SwiperSlide
                    key={index}
                    className="bg-slideBg bg-cover bg-no-repeat"
                  >
                    <Link
                      href={item?.redirectLink}
                      prefetch={true}
                      className="group flex flex-col justify-between gap-[30px] rounded-xl bg-aliceBlue/70 p-6 transition-shadow duration-300 ease-in hover:shadow-md lg:flex-row"
                    >
                      <h3 className="w-full font-opt text-xl font-medium leading-6 text-primary transition-colors duration-300 ease-in group-hover:text-lightBlue lg:max-w-[200px] 3xl:text-3xl 4xl:leading-10">
                        {item?.projectName}
                      </h3>
                      <div className="w-full">
                        <Image
                          src={item?.img}
                          alt={item?.projectName}
                          width={400}
                          height={300}
                          loading="lazy"
                          className="h-full w-full rounded-[10px] object-contain transition-transform duration-300 ease-in group-hover:rotate-2 group-hover:scale-105"
                        />
                      </div>
                    </Link>
                  </SwiperSlide>
                ))}
            </Swiper>
          </div>
        </div>
      </div>
      <ClientTestimonial isDark={true} />
      <Faqs />
      <div className="relative bg-black bg-tech-we-work bg-full bg-no-repeat xl:bg-transparent xl:bg-contact">
        <Image
          src="/webp/homePagev2/logov2.webp"
          alt="logo"
          width={380}
          height={380}
          className="absolute left-1/2 top-[10%] hidden -translate-x-1/2 md:block lg:top-[20%]"
        />
        <Image
          src="/webp/homePagev2/logov2.webp"
          alt="logo"
          width={180}
          height={180}
          className="absolute -left-16 bottom-[10%] rotate-[155deg]"
        />
        <Container className="mb-[60px] py-[70px] lg:bg-contain xl:mb-0 xl:pb-[130px] xl:pt-[140px] 3xl:px-[80px]">
          <div className="flex flex-col justify-between gap-10 lg:flex-row lg:items-center">
            <div className="relative z-30 flex w-full max-w-[570px] flex-col gap-3 xl:gap-5">
              <HighlitedDescription
                dangerouslySetInnerHTML={{
                  __html: homePageData?.contactUsFormTitle,
                }}
                className="font-opt text-[45px] font-bold leading-tight text-white 4xl:text-[64px] 4xl:leading-[68px]"
              />
              <p className="font-opt text-base font-normal leading-normal text-white lg:pr-[76px]">
                {homePageData?.contactUsFormDescription}
              </p>
              <div className="flex flex-col gap-[15px]">
                <div className="flex flex-col gap-1">
                  <Link
                    href={`tel:${homePageData?.contactNumber}`}
                    className="flex items-center gap-2 sm:gap-[15px]"
                  >
                    <div className="w-full max-w-max">
                      <Image
                        src={phone}
                        alt="phone-icon"
                        loading="lazy"
                        width={22}
                        height={22}
                      />
                    </div>
                    <span className="font-opt text-base font-light leading-[1.6rem] text-white 5xl:text-lg 5xl:leading-7">
                      {contactDetails?.cno}
                    </span>
                  </Link>
                  <Link
                    href={`mailto: ${homePageData?.contactEmail}`}
                    className="flex items-center gap-2 sm:gap-[15px]"
                  >
                    <div className="w-full max-w-max">
                      <Image
                        src={email}
                        alt="email-icon"
                        loading="lazy"
                        width={22}
                        height={22}
                      />
                    </div>
                    <span className="font-opt text-base font-light leading-[1.6rem] text-white 5xl:text-lg 5xl:leading-7">
                      {contactDetails?.cmail}
                    </span>
                  </Link>
                </div>
              </div>
              <SocialMedia />
              <div className="w-full max-w-[216px]">
                <ClutchWidget />
              </div>
            </div>
            <ProjectInquiry />
          </div>
        </Container>
      </div>
      <Container className="mb-[60px]">
        <div className="mx-auto flex max-w-[692px] flex-col items-center gap-6 4xl:gap-10">
          <Image
            src="/svg/logo.svg"
            alt="nav-logo"
            width={202}
            height={57.09}
            loading="lazy"
            className="w-full max-w-[130px] xl:max-w-[150px]"
          />
          <span className="text-center font-opt text-lg font-normal leading-6 text-optDesc">
            {contactDetails?.clocation}
          </span>
        </div>
      </Container>
    </>
  );
};

export default HomePageV2;
